import { Inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { WINDOW } from 'src/app/core/core.module';
import { EventObserverService } from 'src/app/core/event-observer.service';
import { isEmpty } from 'src/app/shared/utilities/obj-utilities';
import { UnsubscribeOnDestroy } from 'src/app/shared/utilities/virtual-images/unsubscribe-ondestroy';
import { APP_CONFIG, Settings } from '../config/settings';
import { LocalStorageKeys } from './enums/storage-keys.enum';
import { AuthClientConfig, AuthService, GenericError, User } from '@auth0/auth0-angular';
import { AuthErrors } from '../shared/enums/auth-errors.enum';

export class Identity {
    userId: string;
    contactId: number;
    userName: string;
    firstName: string;
    lastName: string;
    permissionsToken: string;
    organizationId: number;
    email: string;
    defaultPage: string;
    userType: string;
    coreAccessToken: string;
    coreRefreshToken: string;
    expiresIn: number;

    get fullName(): string {
        return [this.firstName, this.lastName].join(' ');
    }
}

@Injectable({
    providedIn: 'root'
})
export class IdentityService extends UnsubscribeOnDestroy {
    private readonly _coreAccessToken$ = new BehaviorSubject<string>(null);
    private readonly _refreshToken$ = new BehaviorSubject<string | null>(null);
    private readonly localStorage$: Observable<StorageEvent>;
    private _authService?: AuthService;
    private _tokenExpiresIn: number | null;
    private storageKey: string;
    private _auth0EnforceNovaOAuthFlag = false;
    private _hasOAuth = false;

    //TODO: set private on FF clean-up
    _isInstantiated = false;
    private readonly _allowedAnonymousPages: string[] = [];
    private readonly _loginPages = ['/signin', '/sso'];

    private readonly _identity$ = new Subject<Identity | null>();
    private readonly _authService$ = new ReplaySubject<AuthService>(1);

    get identity$(): Observable<Identity | null> {
        return this._identity$.asObservable().pipe(
            distinctUntilChanged((p, c) => p?.userId === c?.userId),
            takeUntil(this._cleanup$)
        );
    }

    get authService$(): Observable<AuthService> {
        return this._authService$;
    }
    get loginPages(): string[] {
        if (!this.enforceOAuth) {
            return [];
        }
        return this._loginPages;
    }
    get isAuthenticated$(): Observable<boolean> {
        if (!this.enforceOAuth) {
            return of(false);
        }
        return this._authService$.pipe(switchMap((authService) => authService.isAuthenticated$));
    }
    get user$(): Observable<User | null | undefined> {
        if (!this.enforceOAuth) {
            return of(undefined);
        }
        return this._authService$.pipe(switchMap((authService) => authService.user$));
    }

    get enforceOAuth() {
        return this._auth0EnforceNovaOAuthFlag;
    }

    get hasOAuth() {
        return this._hasOAuth;
    }

    constructor(
        @Inject(APP_CONFIG) private readonly settings: Settings,
        @Inject(WINDOW) private readonly _window: Window,
        private readonly _injector: Injector,
        private readonly _authConfig: AuthClientConfig,
        private readonly _eventObserverService: EventObserverService,
        private readonly _router: Router
    ) {
        super();

        this.storageKey = settings.appName + '.identity';

        this.localStorage$ = this._eventObserverService.observe<StorageEvent>(this._window, 'storage').pipe(
            filter((event) => event.storageArea === localStorage),
            takeUntil(this._cleanup$)
        );

        this.localStorage$
            .pipe(
                filter((event) => event.key === LocalStorageKeys.ACCESS_TOKEN),
                map((event) => event.newValue)
            )
            .subscribe((value) => {
                this.coreAccessToken = value;
                if (isEmpty(value)) {
                    this._router.navigate(['/signin'], { replaceUrl: true });
                }
            });

        this.localStorage$
            .pipe(
                filter((event) => event.key === LocalStorageKeys.REFRESH_TOKEN),
                map((event) => event.newValue)
            )
            .subscribe((value) => {
                this.refreshToken = value;
            });

        this.localStorage$
            .pipe(
                filter((event) => event.key === this.storageKey),
                map((event) => event.newValue)
            )
            .subscribe((value) => {
                const parsedUser = value ? (JSON.parse(value) as Identity) : null;
                this.set(parsedUser);
            });
    }

    init(auth0EnforceNovaOAuthFlag: boolean) {
        if (this._isInstantiated) {
            return;
        }
        this._isInstantiated = true;

        this._auth0EnforceNovaOAuthFlag = auth0EnforceNovaOAuthFlag;

        this._authConfig.set({
            domain: this.settings.authDomain,
            clientId: this.settings.authClientId,
            authorizationParams: {
                audience: `${this._window.location.protocol}${this.settings.coreAPIUrl}`
            },
            useRefreshTokens: true,
            cacheLocation: this.settings.authCacheLocation,
            skipRedirectCallback: window.location.hash?.startsWith('#/signin'),
            httpInterceptor: {
                allowedList: [
                    {
                        uri: `${this.settings.coreAPIUrl}/azureAyaCoreHub*`,
                        allowAnonymous: true
                    },
                    {
                        uri: `${this.settings.coreAPIUrl}*`,
                        tokenOptions: {
                            authorizationParams: {
                                audience: `${this._window.location.protocol}${this.settings.coreAPIUrl}`
                            }
                        },
                        allowAnonymous: !this.enforceOAuth
                    }
                ]
            }
        });
        this._authService = this._injector.get(AuthService);
        this._authService$.next(this._authService);
        if (this.enforceOAuth) {
            this.processErrors();
            this._authService.isAuthenticated$.pipe(takeUntil(this._cleanup$)).subscribe((isAuth: boolean) => {
                this._hasOAuth = isAuth;
            });
        }
    }

    logout() {
        if (!this.enforceOAuth) {
            return;
        }
        this.delete();
        this._authService?.logout({
            logoutParams: {
                returnTo: this._window.location.origin
            }
        });
        this._router.navigate(['/signin'], {
            queryParams: { loading: this.enforceOAuth ? this.enforceOAuth : undefined },
            replaceUrl: true
        });
    }

    //Clears any expired auth data. Ex: refresh token.
    //For more info please check: "Clear Expired Refresh token before login"
    clearCachedTokens() {
        if (!this.enforceOAuth) {
            return;
        }
        this._authService?.logout({
            openUrl: false
        });
    }

    getSignInPage(): string {
        if (!this.enforceOAuth) {
            return '';
        }
        const targetPath = this.getCurrentLocationPath();
        const url = '/signin' + (targetPath.length > 0 ? `?target=${encodeURIComponent(targetPath)}` : '');
        return url;
    }

    getCurrentLocationPath() {
        if (!this.enforceOAuth) {
            return '';
        }
        let path = this._window?.location?.hash?.replace('#', '') ?? this._window?.location?.pathname ?? '';
        if (!path.replaceAll('/', '').replaceAll(' ', '')) {
            path = '';
        }
        return path.split('?')[0];
    }

    private processErrors() {
        if (!this.enforceOAuth) {
            return;
        }
        if (!this._authService) {
            return;
        }
        this._authService.error$
            .pipe(
                map((e) => e as GenericError),
                map((e: GenericError) => {
                    const pathRoute = this.getCurrentLocationPath().toLowerCase();
                    const isAnonymousPage = this._allowedAnonymousPages
                        .map((p) => p.toLowerCase())
                        .some((p) => pathRoute.startsWith(p));
                    const isLoginPage = this._loginPages
                        .map((p) => p.toLowerCase())
                        .some((p) => pathRoute.startsWith(p));
                    return { error: e?.error, isAnonymousPage, isLoginPage };
                }),
                tap(({ error, isAnonymousPage, isLoginPage }) => {
                    if ((isAnonymousPage || isLoginPage) && error === AuthErrors.InvalidGrant) {
                        this.clearCachedTokens();
                        this._window.location.reload();
                    }
                }),
                filter(
                    ({ error, isAnonymousPage, isLoginPage }) =>
                        (error === AuthErrors.LoginRequired ||
                            error === AuthErrors.InvalidGrant ||
                            error === AuthErrors.MissingRefreshToken) &&
                        !isAnonymousPage &&
                        !isLoginPage
                ),
                switchMap(({ error }) =>
                    this.isAuthenticated$.pipe(
                        map((isAuthenticated) => ({
                            loginRequired: error === AuthErrors.LoginRequired || error === AuthErrors.InvalidGrant,
                            unauthorized: !isAuthenticated
                        }))
                    )
                ),
                filter(({ loginRequired, unauthorized }) => loginRequired || unauthorized),
                take(1)
            )
            .subscribe(() => this.redirectToSignInPage());
    }

    private redirectToSignInPage(): void {
        if (!this.enforceOAuth) {
            return;
        }
        const url = this.getSignInPage();
        this._router.navigateByUrl(url);
    }

    get tokenExpiresIn(): number | null {
        if (!this._tokenExpiresIn) {
            const storageItem = this._window.localStorage.getItem(LocalStorageKeys.TOKEN_EXPIRES_IN);
            if (storageItem) {
                this._tokenExpiresIn = parseInt(storageItem, 10);
            }
        }
        return this._tokenExpiresIn;
    }

    set tokenExpiresIn(sec: number | null) {
        // TODO: look into this further to understand if we need better/less arbitrary default value
        sec ??= 1;
        const val = Date.now() + sec * 1000;
        this._window.localStorage.setItem(LocalStorageKeys.TOKEN_EXPIRES_IN, val.toString());
        this._tokenExpiresIn = val;
    }

    get current(): Identity {
        //TODO - create local storage service
        const storedIdentity = this._window.localStorage[this.storageKey];
        if (storedIdentity) {
            try {
                const data = JSON.parse(storedIdentity);
                this._identity$.next(Object.assign(new Identity(), data));
                this._coreAccessToken$.next(data.coreAccessToken);
                return data;
            } catch (e) {
                console.error(e);
            }
        }
        return null;
    }

    get coreAccessToken$(): Observable<string> {
        return this._authService$.pipe(
            take(1),
            switchMap((authService) => {
                if (this.enforceOAuth) {
                    return authService.getAccessTokenSilently();
                }
                return this._coreAccessToken$.asObservable();
            }),
            distinctUntilChanged()
        );
    }

    get coreAccessToken(): string | null {
        if (!this._coreAccessToken$.value) {
            const token = this._window.localStorage.getItem(LocalStorageKeys.ACCESS_TOKEN);
            this._coreAccessToken$.next(token);
            return token;
        }

        return this._coreAccessToken$?.value;
    }

    set coreAccessToken(val: string | null) {
        if (isEmpty(val)) {
            this._window.localStorage.removeItem(LocalStorageKeys.ACCESS_TOKEN);
        } else if (val) {
            this._window.localStorage.setItem(LocalStorageKeys.ACCESS_TOKEN, val);
        }
        this._coreAccessToken$.next(val);
    }

    get refreshToken(): string | null {
        if (!this._refreshToken$.value) {
            const token = this._window.localStorage.getItem(LocalStorageKeys.REFRESH_TOKEN);
            this._refreshToken$.next(token);
            return token;
        }
        return this._refreshToken$.value;
    }

    set refreshToken(val: string | null) {
        if (isEmpty(val)) {
            this._window.localStorage.removeItem(LocalStorageKeys.REFRESH_TOKEN);
        } else if (val) {
            this._window.localStorage.setItem(LocalStorageKeys.REFRESH_TOKEN, val);
        }
        this._refreshToken$.next(val);
    }

    get isAccessTokenSilentRefreshLocked(): boolean | null {
        return this._window.localStorage.getItem(LocalStorageKeys.SILENT_REFRESH_LOCKED) === 'true';
    }

    set isAccessTokenSilentRefreshLocked(isLocked: boolean | null) {
        this._window.localStorage.setItem(LocalStorageKeys.SILENT_REFRESH_LOCKED, isLocked.toString());
    }

    delete() {
        this._window.localStorage.removeItem(this.storageKey);
        this._window.localStorage.removeItem(LocalStorageKeys.SILENT_REFRESH_AVAILABLE_DATE);
        this._window.localStorage.removeItem(LocalStorageKeys.SILENT_REFRESH_LOCKED);
        this._window.localStorage.removeItem(LocalStorageKeys.TOKEN_EXPIRES_IN);
        this._window.localStorage.removeItem(LocalStorageKeys.ACCESS_TOKEN);
        this._window.localStorage.removeItem(LocalStorageKeys.REFRESH_TOKEN);
        this._identity$.next(null);
    }

    exists(): boolean {
        if (this.enforceOAuth) {
            return this.hasOAuth && this.current !== null;
        }
        const ident = this.current;
        return ident !== null && ident.coreAccessToken !== null;
    }

    set(data: Identity) {
        if (data && data.userName && data.permissionsToken) {
            //save to local storage
            this._window.localStorage[this.storageKey] = JSON.stringify(data);
            this._identity$.next(Object.assign(new Identity(), data));
        } else {
            this._identity$.next(null);
        }
    }

    hasPermission(moduleName: string, moduleItemName: string): boolean {
        if (!this.exists()) {
            return false;
        }

        const permissionsToken = this.current.permissionsToken.toLowerCase();
        const permissions = permissionsToken.toLowerCase().split(',');
        const permissionName = `${String(moduleName)}:${String(moduleItemName)}`.toLocaleLowerCase();

        const result = permissions.some((p) => p.startsWith(permissionName));
        return result;
    }

    // Used in ticketing permissions. Check if it can be removed.
    authorize(resource, action) {
        let module = '';
        let item = '';
        let isAuthorized = false;

        [module, item] = resource.toLowerCase().split('.');
        const tokenItem = `${module}:${item}`;

        if (this.current) {
            if (item) {
                switch (action) {
                    case 'c': // 'c' the identity.permissionsToken must have '$c'
                        isAuthorized = this.current.permissionsToken.toLowerCase().includes(tokenItem + '$c');
                        break;
                    case 'd': // 'd' the identity.permissionsToken can have '$c' or '$d'
                        isAuthorized =
                            this.current.permissionsToken.toLowerCase().includes(tokenItem + '$c') ||
                            this.current.permissionsToken.toLowerCase().includes(tokenItem + '$d');
                        break;
                    case 'u': // 'u' identity.permissionsToken can have '$c', '$d' or '$u'
                        isAuthorized =
                            this.current.permissionsToken.toLowerCase().includes(tokenItem + '$c') ||
                            this.current.permissionsToken.toLowerCase().includes(tokenItem + '$d') ||
                            this.current.permissionsToken.toLowerCase().includes(tokenItem + '$u');
                        break;
                    case 'r': // 'r' identity.permissionsToken can '$c', '$d', '$u' or '$r'
                        isAuthorized = this.current.permissionsToken.toLowerCase().includes(tokenItem + '$');
                        break;
                }
            }
        }
        return isAuthorized;
    }
}
